import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { fmtMoney, fmtMoney0 } from './currency';

const fmt = fmtMoney;
const fmtShort = fmtMoney0;

/**
 * Generate a professional PDF of the dashboard financial overview.
 * Includes: company header, KPI summary, revenue vs expenses trend,
 * top customers, top products, recent transactions, and account balances.
 */
export default function generateFinancialPDF({ kpi, balances, salesTrend, expenseTrend, topCustomers, topProducts, recentTransactions, companyName }) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;
  const contentW = pageW - margin * 2;
  let y = margin;

  // Colors
  const primary = [15, 39, 71];    // Deep navy
  const accent = [37, 99, 235];    // Blue
  const green = [22, 163, 74];
  const red = [220, 38, 38];
  const gray = [100, 116, 139];
  const lightGray = [241, 245, 249];

  // Helper: add a section header
  const sectionHeader = (title, yPos) => {
    doc.setFillColor(...accent);
    doc.rect(margin, yPos, contentW, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(title, margin + 4, yPos + 5.5);
    doc.setTextColor(30, 41, 59);
    return yPos + 12;
  };

  // Helper: draw a horizontal line
  const line = (yPos) => {
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, pageW - margin, yPos);
    return yPos + 2;
  };

  // ---- HEADER ----
  doc.setFillColor(...primary);
  doc.rect(0, 0, pageW, 28, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(companyName || 'LedgerPro', margin, 12);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Financial Overview', margin, 19);
  doc.setFontSize(8);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, margin, 25);
  // Right-aligned period
  doc.setFontSize(9);
  doc.text(`Year: ${new Date().getFullYear()}`, pageW - margin, 12, { align: 'right' });
  y = 34;

  // ---- KPI SUMMARY ----
  y = sectionHeader('KEY PERFORMANCE INDICATORS', y);

  const net = kpi?.net_profit ?? 0;
  const kpiRows = [
    ['Revenue (YTD)', fmtShort(kpi?.revenue?.year_to_date), 'This month', fmtShort(kpi?.revenue?.this_month)],
    ['Gross Profit', fmtShort(kpi?.gross_profit), '', ''],
    ['Net Profit', fmtShort(net), net >= 0 ? 'Profitable' : 'Loss', ''],
    ['Cash & Bank', fmtShort(kpi?.cash), '', ''],
    ['Accounts Receivable', fmtShort(kpi?.accounts_receivable), `Outstanding: ${kpi?.outstanding_invoices || 0} invoices`, ''],
    ['Accounts Payable', fmtShort(kpi?.accounts_payable), `Outstanding: ${kpi?.outstanding_bills || 0} bills`, ''],
    ['Inventory Value', fmtShort(kpi?.inventory_value), '', ''],
    ['Expenses (YTD)', fmtShort(kpi?.expenses?.year_to_date), 'This month', fmtShort(kpi?.expenses?.this_month)],
  ];

  doc.autoTable({
    startY: y,
    head: [['Metric', 'Amount', 'Detail', '']],
    body: kpiRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: 3, textColor: [30, 41, 59] },
    headStyles: { fillColor: primary, fontStyle: 'bold', fontSize: 9 },
    alternateRowStyles: { fillColor: lightGray },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55 },
      1: { halign: 'right', cellWidth: 40 },
      2: { cellWidth: 45, textColor: gray },
      3: { halign: 'right', cellWidth: 40, textColor: gray },
    },
    theme: 'grid',
    tableLineColor: [226, 232, 240],
  });
  y = doc.lastAutoTable.finalY + 6;

  // ---- REVENUE VS EXPENSES TREND ----
  if (salesTrend?.length || expenseTrend?.length) {
    y = sectionHeader('REVENUE VS EXPENSES (Last 6 Months)', y);

    const months = [...new Set([...(salesTrend || []).map(s => s.month), ...(expenseTrend || []).map(e => e.month)])].sort();
    const trendRows = months.map(m => {
      const sale = (salesTrend || []).find(s => s.month === m);
      const exp = (expenseTrend || []).find(e => e.month === m);
      const [yr, mo] = m.split('-');
      const label = new Date(yr, parseInt(mo, 10) - 1).toLocaleString('en-US', { month: 'short', year: '2-digit' });
      return [label, fmtShort(sale?.revenue), fmtShort(exp?.total_expenses)];
    });

    doc.autoTable({
      startY: y,
      head: [['Month', 'Revenue', 'Expenses']],
      body: trendRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3, textColor: [30, 41, 59] },
      headStyles: { fillColor: accent, fontStyle: 'bold', fontSize: 9 },
      alternateRowStyles: { fillColor: lightGray },
      columnStyles: { 0: { cellWidth: 50 }, 1: { halign: 'right', cellWidth: 55 }, 2: { halign: 'right', cellWidth: 55 } },
      theme: 'grid',
      tableLineColor: [226, 232, 240],
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // Check if we need a new page
  if (y > 250) { doc.addPage(); y = margin; }

  // ---- TOP CUSTOMERS ----
  if (topCustomers?.length) {
    y = sectionHeader('TOP CUSTOMERS', y);
    const custRows = topCustomers.map(c => [c.name, fmtShort(c.total_sales)]);
    doc.autoTable({
      startY: y,
      head: [['Customer', 'Total Sales']],
      body: custRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3, textColor: [30, 41, 59] },
      headStyles: { fillColor: [22, 163, 74], fontStyle: 'bold', fontSize: 9 },
      alternateRowStyles: { fillColor: lightGray },
      columnStyles: { 0: { cellWidth: 100 }, 1: { halign: 'right', cellWidth: 65 } },
      theme: 'grid',
      tableLineColor: [226, 232, 240],
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ---- TOP PRODUCTS ----
  if (topProducts?.length) {
    if (y > 250) { doc.addPage(); y = margin; }
    y = sectionHeader('TOP PRODUCTS', y);
    const prodRows = topProducts.map(p => [p.name, fmtShort(p.total_sales)]);
    doc.autoTable({
      startY: y,
      head: [['Product', 'Total Sales']],
      body: prodRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3, textColor: [30, 41, 59] },
      headStyles: { fillColor: [13, 148, 136], fontStyle: 'bold', fontSize: 9 },
      alternateRowStyles: { fillColor: lightGray },
      columnStyles: { 0: { cellWidth: 100 }, 1: { halign: 'right', cellWidth: 65 } },
      theme: 'grid',
      tableLineColor: [226, 232, 240],
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ---- ACCOUNT BALANCES ----
  if (y > 250) { doc.addPage(); y = margin; }
  y = sectionHeader('ACCOUNT BALANCES', y);
  const cash = balances?.cash || 0;
  const bank = balances?.bank || 0;
  const ar = kpi?.accounts_receivable || 0;
  const ap = kpi?.accounts_payable || 0;
  const balRows = [
    ['Cash', fmtShort(cash)],
    ['Bank Accounts', fmtShort(bank)],
    ['Accounts Receivable', fmtShort(ar)],
    ['Accounts Payable', fmtShort(ap)],
    ['Current Balance', fmtShort(cash + bank + ar - ap)],
  ];
  doc.autoTable({
    startY: y,
    head: [['Account', 'Balance']],
    body: balRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: 3, textColor: [30, 41, 59] },
    headStyles: { fillColor: primary, fontStyle: 'bold', fontSize: 9 },
    alternateRowStyles: { fillColor: lightGray },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 100 }, 1: { halign: 'right', cellWidth: 65 } },
    theme: 'grid',
    tableLineColor: [226, 232, 240],
  });
  y = doc.lastAutoTable.finalY + 6;

  // ---- RECENT TRANSACTIONS ----
  if (recentTransactions?.length) {
    if (y > 240) { doc.addPage(); y = margin; }
    y = sectionHeader('RECENT TRANSACTIONS', y);
    const txnRows = recentTransactions.map(t => [
      t.entry_number,
      t.entry_date || '',
      (t.description || '').substring(0, 40),
      t.source_module || '',
      t.total_debit ? fmtShort(t.total_debit) : '',
      t.total_credit ? fmtShort(t.total_credit) : '',
      t.status || '',
    ]);
    doc.autoTable({
      startY: y,
      head: [['Entry #', 'Date', 'Description', 'Module', 'Debit', 'Credit', 'Status']],
      body: txnRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 2, textColor: [30, 41, 59] },
      headStyles: { fillColor: [100, 116, 139], fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: lightGray },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 22 },
        2: { cellWidth: 45 },
        3: { cellWidth: 20 },
        4: { halign: 'right', cellWidth: 22 },
        5: { halign: 'right', cellWidth: 22 },
        6: { cellWidth: 18 },
      },
      theme: 'grid',
      tableLineColor: [226, 232, 240],
    });
  }

  // ---- FOOTER ----
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFillColor(...primary);
    doc.rect(0, doc.internal.pageSize.getHeight() - 10, pageW, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${companyName || 'LedgerPro'} · Financial Overview`, margin, doc.internal.pageSize.getHeight() - 3.5);
    doc.text(`Page ${i} of ${totalPages}`, pageW - margin, doc.internal.pageSize.getHeight() - 3.5, { align: 'right' });
  }

  // Save
  const date = new Date().toISOString().slice(0, 10);
  doc.save(`financial-overview-${date}.pdf`);
}
